/**
 * Utility functions for percentage-based weightage management
 */

/**
 * Convert slider value (1-10) to percentage
 * @param {number} sliderValue - Value from slider (1-10)
 * @param {number} min - Minimum slider value (default: 1)
 * @param {number} max - Maximum slider value (default: 10)
 * @returns {number} Percentage value (0-100)
 */
export const sliderValueToPercentage = (sliderValue, min = 1, max = 10) => {
  if (sliderValue === 0) return 0;
  return ((sliderValue - min) / (max - min)) * 100;
};

/**
 * Convert percentage to slider value (1-10)
 * @param {number} percentage - Percentage value (0-100)
 * @param {number} min - Minimum slider value (default: 1)
 * @param {number} max - Maximum slider value (default: 10)
 * @returns {number} Slider value (1-10)
 */
export const percentageToSliderValue = (percentage, min = 1, max = 10) => {
  if (percentage === 0) return 0;
  const sliderValue = (percentage / 100) * (max - min) + min;
  return Math.round(sliderValue);
};

/**
 * Calculate total percentage from weightages
 * @param {Object} weightages - Object with weightage values
 * @returns {number} Total percentage
 */
export const calculateTotalPercentage = (weightages) => {
  return Object.values(weightages).reduce((sum, value) => sum + value, 0);
};

/**
 * Update weightages without auto-redistribution
 * @param {Object} weightages - Current weightages object
 * @param {string} changedKey - Key of the weightage that was changed
 * @param {number} newValue - New value for the changed weightage
 * @returns {Object} Updated weightages
 */
export const redistributeWeightages = (weightages, changedKey, newValue) => {
  const updatedWeightages = { ...weightages };
  updatedWeightages[changedKey] = Math.min(100, Math.max(0, newValue));
  return updatedWeightages;
};

/**
 * Normalize weightages to ensure they sum to 100%
 * @param {Object} weightages - Weightages object
 * @returns {Object} Normalized weightages
 */
export const normalizeWeightages = (weightages) => {
  const total = calculateTotalPercentage(weightages);
  
  if (total === 0) {
    // If all weightages are 0, distribute equally
    const keys = Object.keys(weightages);
    const equalPercentage = 100 / keys.length;
    const normalized = {};
    keys.forEach(key => {
      normalized[key] = equalPercentage;
    });
    return normalized;
  }

  // Normalize to 100%
  const normalized = {};
  Object.keys(weightages).forEach(key => {
    normalized[key] = (weightages[key] / total) * 100;
  });

  return normalized;
};

/**
 * Convert percentage weightages to slider values
 * @param {Object} percentageWeightages - Weightages in percentage
 * @returns {Object} Slider values (1-10)
 */
export const percentageToSliderWeightages = (percentageWeightages) => {
  const sliderWeightages = {};
  Object.keys(percentageWeightages).forEach(key => {
    sliderWeightages[key] = percentageToSliderValue(percentageWeightages[key]);
  });
  return sliderWeightages;
};

/**
 * Convert slider weightages to percentage
 * @param {Object} sliderWeightages - Weightages as slider values (1-10)
 * @returns {Object} Percentage weightages
 */
export const sliderToPercentageWeightages = (sliderWeightages) => {
  const percentageWeightages = {};
  Object.keys(sliderWeightages).forEach(key => {
    percentageWeightages[key] = sliderValueToPercentage(sliderWeightages[key]);
  });
  return percentageWeightages;
};

/**
 * Validate percentage input
 * @param {string} value - Input value
 * @returns {Object} Validation result
 */
export const validatePercentageInput = (value) => {
  // Allow empty string for typing
  if (value === '') {
    return { isValid: true, value: 0 };
  }
  
  const numValue = parseFloat(value);
  
  if (isNaN(numValue)) {
    return { isValid: false, error: 'Please enter a valid number' };
  }
  
  if (numValue < 0) {
    return { isValid: false, error: 'Percentage cannot be negative' };
  }
  
  if (numValue > 100) {
    return { isValid: false, error: 'Percentage cannot exceed 100%' };
  }
  
  return { isValid: true, value: numValue };
};

/**
 * Get weightage distribution summary
 * @param {Object} weightages - Weightages object
 * @returns {Object} Summary with total and distribution info
 */
export const getWeightageSummary = (weightages) => {
  const total = calculateTotalPercentage(weightages);
  const activeWeightages = Object.entries(weightages).filter(([_, value]) => value > 0);
  
  return {
    total,
    activeCount: activeWeightages.length,
    isBalanced: Math.abs(total - 100) < 0.01, // Allow small floating point errors
    distribution: activeWeightages.map(([key, value]) => ({
      key,
      value,
      percentage: value
    }))
  };
};
